import { CartContextInterface, CartItem } from './cartContext'
import { useCallback, useContext, useState } from 'react'
import { v4 as uuid } from 'uuid'
import useSessionStorage from 'use-session-storage-state'
import { AuthContext } from '../../../components/auth/AuthContext'
import ShopifyClient from '../../../lib/shopify'
import { MessageContext } from '../../messages/context/messageContext'
import { useCartBundle } from '../../../components/buyButton/useCartBundle'

export const useCartContext = (): CartContextInterface => {
  const [items, setItems] = useSessionStorage<CartItem[]>('cartItems', {
    defaultValue: [],
  })
  const [loading, setLoading] = useState(false)
  const { currentUser } = useContext(AuthContext)
  const { addMessage } = useContext(MessageContext)

  const addItem = useCallback(
    (p: CartItem) => {
      setItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.sku === p.sku)
        const newItem = {
          ...p,
          id: uuid(),
          qty: existingItem ? existingItem.qty + p.qty : p.qty,
        }
        if (existingItem) {
          return [
            ...prevItems.filter((item) => item.id !== existingItem.id),
            { ...newItem, id: existingItem.id },
          ]
        } else {
          return [...prevItems, newItem]
        }
      })
      addMessage({ body: 'Tuote lisätty koriin', type: 'success' })
    },
    [setItems, addMessage]
  )
  const editItem = useCallback(
    (p: CartItem) => {
      const { id } = p
      setItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.id === id)
        if (existingItem) {
          return [...prevItems.filter((item) => item.id !== existingItem.id), p]
        } else {
          return prevItems
        }
      })
    },
    [setItems]
  )
  const removeItem = useCallback(
    (p: CartItem) => {
      const { id } = p
      setItems((prevItems) => [...prevItems.filter((item) => item.id !== id)])
    },
    [setItems]
  )
  const clear = useCallback(() => {
    setItems([])
  }, [setItems])

  const { getBundledLineItems } = useCartBundle()

  const order = async (): Promise<{ redirectUrl: string }> => {
    setLoading(true)
    const email = null !== currentUser && currentUser?.email
    const bundledLineItems = items.flatMap((item) =>
      getBundledLineItems(item.sku)
    )
    const input = {
      lineItems: [
        ...items.map((item) => ({
          variantId: item.shopifyStoreFrontId,
          quantity: item.qty,
        })),
        ...bundledLineItems,
      ],
      email: email || undefined,
    }

    const { webUrl: redirectUrl } = await ShopifyClient.checkout.create(input)
    setLoading(false)
    return { redirectUrl }
  }
  return {
    addItem,
    editItem,
    removeItem,
    clear,
    items,
    order,
    loading,
  }
}
